import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import {
    currentStateText,
    reasonText,
    previewExtraColumns,
    serialStatusPresentation,
    withPastedValues,
} from "./workOrderHelpers";
import { STATUS_BODY_INK, STATUS_PALETTE, STATUS_SURFACE } from "../statusPalette";
import { FORM_TOKENS } from "./WorkOrderFormShared";

// The per-serial verdict table.
//
// THIS COMPONENT IS THE REASON THERE IS NO PRE-FLIGHT VALIDATION ENDPOINT.
//
// createWorkOrder answers a bad request with a 422 whose body is
// {summary, serials[]} -- one row per serial, with a status and a reason -- and
// reviewWorkOrder answers a stale approval with a 409 carrying the SAME body,
// because custody can move in the days between a request being raised and
// somebody signing it off. Submit is therefore where a maker learns a serial is
// bad, and this table is the whole of that answer: it renders INLINE, beside
// the serials the user entered, with the form still on screen and still filled
// in, so the fix is "delete two chips and press Submit again".
//
// Flattened into a toast string, that design collapses -- the user is told
// "3 serials could not be resolved" and has no way to find out which three.
// Nothing here may be reduced to `responseStatus.msg`.
//
// Detection of the shape is the CALLER's job, through isSerialReport() from
// Api/WorkOrderApi.js and summariseSerialReport() from workOrderHelpers: the
// guard is the body, not the status code, because 422 and 409 both produce it.

// The colour comes from serialStatusPresentation, which is the one place a
// status is turned into a label, a colour and a verdict.
const StatusPill = ({ status, label }) => {
    const { theme } = serialStatusPresentation(status);
    return (
        <Box
            component="span"
            sx={{
                display: "inline-block",
                px: 1,
                py: "2px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                color: theme.color,
                backgroundColor: theme.background,
            }}
        >
            {label}
        </Box>
    );
};

const SummaryChip = ({ label, value, emphasis }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: "6px",
            border: "1px solid",
            borderColor: emphasis ? STATUS_PALETTE.error.border : FORM_TOKENS.fieldBorder,
            backgroundColor: emphasis ? STATUS_PALETTE.error.bg : FORM_TOKENS.surface,
        }}
    >
        <Typography
            sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: emphasis ? FORM_TOKENS.danger : FORM_TOKENS.ink,
            }}
        >
            {value}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel }}>{label}</Typography>
    </Box>
);

// How many problem rows are shown before the table collapses behind a toggle.
// A 2000-serial request that fails wholesale must not render 2000 rows into a
// dialog the user then cannot scroll out of.
const COLLAPSED_ROW_COUNT = 25;

// The two skins. `error` is a failed submit or a refused approval; `neutral`
// is the answer to a question the user ASKED -- the explicit serials check,
// which is not a failure and must not be painted as one. The counts and the
// per-row verdicts are identical either way; only the frame around them says
// whether anything went wrong.
const TONES = {
    error: {
        border: STATUS_PALETTE.error.border,
        background: STATUS_SURFACE.error,
        title: FORM_TOKENS.danger,
    },
    neutral: {
        border: FORM_TOKENS.fieldBorder,
        background: FORM_TOKENS.sunk,
        title: FORM_TOKENS.ink,
    },
};

const SerialReportTable = ({
    report,
    title = "These serials could not be accepted",
    // Optional. The create form passes a handler that drops the offending
    // chips; the review dialog passes none, because a checker cannot edit
    // somebody else's request -- they can only reject it and say why.
    onRemoveSerials = null,
    removeLabel = "Remove these serials from the form",
    testIdPrefix = "work-order-serial-report",
    // "error" by default, so the two existing callers -- the failed submit and
    // the refused approval -- are untouched.
    tone = "error",
    // A second line under the title, for something true of the REPORT rather
    // than of any row in it.
    caption = "",
    // The parsed box, when there is one. Two jobs, both optional: it decides
    // whether the table grows Item Name and SKU columns, and it supplies their
    // values -- the API returns neither. The review dialog passes nothing and
    // gets the four-column table it always had.
    paste = null,
    // STALE. The box no longer matches what this answer describes, so it is
    // greyed and its one action is withheld: a table that reads as current
    // while describing a list that has changed is worse than no table, and
    // "remove these serials" against a stale verdict would strip the wrong
    // ones.
    dimmed = false,
}) => {
    const [showAll, setShowAll] = useState(false);

    // Collapsed again for each new report. Without this a second submit opened
    // expanded, because the toggle belonged to the previous failure.
    useEffect(() => {
        setShowAll(false);
    }, [report]);

    // Problems first, in the server's order within each group. The eligible
    // rows are kept -- they are how the maker sees that the other forty-eight
    // serials were fine and only two need touching -- but they are never what
    // the user has to scroll past to reach the failures.
    const orderedRows = useMemo(() => {
        // Joined BEFORE the ordering, so a row carries its pasted columns
        // whichever group it ends up in.
        const rows = withPastedValues(report?.rows || [], paste);
        return [
            ...rows.filter((r) => r.isProblem),
            ...rows.filter((r) => !r.isProblem),
        ];
    }, [report, paste]);

    // Present only for a movement type whose paste carries them. A Forward
    // must not grow two permanently empty columns.
    const extraColumns = useMemo(() => previewExtraColumns(paste), [paste]);

    const visibleRows = showAll
        ? orderedRows
        : orderedRows.slice(0, COLLAPSED_ROW_COUNT);

    // Nothing to render is a real state: an ordinary message failure is not a
    // serial report, and the caller shows its toast instead.
    if (!report || !orderedRows.length) return null;

    const { summary, problemSerials } = report;
    const warnings = Array.isArray(report.warnings) ? report.warnings : [];
    const theme = TONES[tone] || TONES.error;

    return (
        <Box
            data-testid={testIdPrefix}
            data-stale={dimmed ? "true" : "false"}
            sx={{
                border: `1px solid ${dimmed ? FORM_TOKENS.dashedBorder : theme.border}`,
                borderStyle: dimmed ? "dashed" : "solid",
                backgroundColor: dimmed ? FORM_TOKENS.sunk : theme.background,
                borderRadius: "8px",
                p: 2,
                mt: 2,
                // Greyed, not hidden: the user still has to be able to read
                // which serials the out-of-date answer covered.
                opacity: dimmed ? 0.55 : 1,
                filter: dimmed ? "grayscale(1)" : "none",
            }}
        >
            <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: theme.title, mb: 0.5 }}
            >
                {title}
            </Typography>
            {caption ? (
                <Typography
                    data-testid={`${testIdPrefix}-caption`}
                    sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel, mb: 0.5 }}
                >
                    {caption}
                </Typography>
            ) : null}
            {report.message ? (
                <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel, mb: 0.5 }}>
                    {report.message}
                </Typography>
            ) : null}

            {/* The one number the maker acts on, said in words above the
                counts. problemSerials is computed for this line. */}
            {problemSerials.length > 0 ? (
                <Typography
                    data-testid={`${testIdPrefix}-problem-count`}
                    sx={{ fontSize: "13px", color: FORM_TOKENS.danger, mb: 1.5 }}
                >
                    {problemSerials.length} of {summary.total} serial
                    {summary.total === 1 ? "" : "s"} need attention.
                </Typography>
            ) : null}

            {/* The ADVISORY half of the report, above the table and in its
                own colour. These never block a submit: the commonest one says
                the serials sit at several different origins, which is allowed
                -- a consolidation return legitimately pulls from four
                sub-dealers -- and reading it as a failure is exactly the
                mistake the separate styling exists to prevent. */}
            {warnings.length > 0 && (
                <Box
                    data-testid={`${testIdPrefix}-warnings`}
                    sx={{
                        border: `1px solid ${STATUS_PALETTE.warningSoft.border}`,
                        backgroundColor: STATUS_SURFACE.warning,
                        borderRadius: "6px",
                        px: 1.5,
                        py: 1,
                        mb: 1.5,
                    }}
                >
                    <Typography
                        sx={{ fontSize: "12px", fontWeight: 600, color: STATUS_PALETTE.warning.fg, mb: 0.25 }}
                    >
                        Worth checking, but nothing is blocked
                    </Typography>
                    {warnings.map((warning, index) => (
                        <Typography
                            key={`${warning}-${index}`}
                            data-testid={`${testIdPrefix}-warning-${index}`}
                            sx={{ fontSize: "12px", color: STATUS_BODY_INK.warning, lineHeight: "18px" }}
                        >
                            {warning}
                        </Typography>
                    ))}
                </Box>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                <SummaryChip label="submitted" value={summary.total} />
                <SummaryChip label="ready" value={summary.eligible} />
                <SummaryChip
                    label="not allowed"
                    value={summary.ineligible}
                    emphasis={summary.ineligible > 0}
                />
                {/* Never emphasised. The server only emits `new` on a
                    movement type that registers what it moves, so an unknown
                    serial here is the job rather than a typo. */}
                <SummaryChip label="to be registered" value={summary.new} />
                <SummaryChip
                    label="repeated"
                    value={summary.duplicate}
                    emphasis={summary.duplicate > 0}
                />
            </Box>

            <Box sx={{ overflowX: "auto", maxHeight: 360, overflowY: "auto" }}>
                <Box
                    component="table"
                    sx={{
                        width: "100%",
                        borderCollapse: "collapse",
                        backgroundColor: FORM_TOKENS.surface,
                        "& th, & td": {
                            textAlign: "left",
                            padding: "8px 12px",
                            borderBottom: `1px solid ${FORM_TOKENS.bandRule}`,
                            verticalAlign: "top",
                        },
                        "& th": {
                            fontSize: "12px",
                            fontWeight: 600,
                            color: FORM_TOKENS.inkLabel,
                            position: "sticky",
                            top: 0,
                            backgroundColor: FORM_TOKENS.surface,
                            zIndex: 1,
                            whiteSpace: "nowrap",
                        },
                        "& td": { fontSize: "13px", color: FORM_TOKENS.ink },
                    }}
                >
                    <thead>
                        <tr>
                            <th>Serial Number</th>
                            {extraColumns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                            ))}
                            <th>Verdict</th>
                            <th>Current State</th>
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((row, index) => (
                            <tr
                                key={`${row.serialNumber}-${index}`}
                                data-testid={`${testIdPrefix}-row-${row.serialNumber}`}
                            >
                                <td style={{ overflowWrap: "anywhere" }}>
                                    {row.serialNumber || "-"}
                                </td>
                                {/* What the user PASTED into that row, so a
                                    shifted column is visible as a wrong value
                                    rather than only as a count. A row the
                                    parse no longer has -- the box was edited
                                    after the preview -- shows a dash rather
                                    than an invented value. */}
                                {extraColumns.map((column) => (
                                    <td
                                        key={column.key}
                                        style={{ overflowWrap: "anywhere" }}
                                        data-testid={`${testIdPrefix}-${column.key}-${row.serialNumber}`}
                                    >
                                        {row.pasted?.[column.key] || "-"}
                                    </td>
                                ))}
                                <td>
                                    <StatusPill
                                        status={row.status}
                                        label={row.statusLabel}
                                    />
                                </td>
                                {/* BOTH COLUMNS ARE CONDITIONAL. They answer
                                    questions that do not always have answers:
                                    an asset being registered has no current
                                    state, and an accepted serial has no reason
                                    to report. Filling those blanks restated
                                    the verdict three times across one row. */}
                                <td style={{ color: FORM_TOKENS.inkLabel, whiteSpace: "nowrap" }}>
                                    {currentStateText(row)}
                                </td>
                                <td
                                    style={{
                                        color: row.isProblem ? FORM_TOKENS.danger : FORM_TOKENS.inkLabel,
                                    }}
                                >
                                    {reasonText(row)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Box>
            </Box>

            {orderedRows.length > COLLAPSED_ROW_COUNT && (
                <Button
                    size="small"
                    data-testid={`${testIdPrefix}-toggle`}
                    onClick={() => setShowAll((prev) => !prev)}
                    sx={{ mt: 1, fontSize: 12, textTransform: "none", color: FORM_TOKENS.purple }}
                >
                    {showAll
                        ? "Show fewer"
                        : `Show all ${orderedRows.length} serials`}
                </Button>
            )}

            {onRemoveSerials && !dimmed && problemSerials.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        data-testid={`${testIdPrefix}-remove`}
                        onClick={() => onRemoveSerials(problemSerials)}
                        sx={{
                            fontSize: 12,
                            textTransform: "none",
                            borderColor: FORM_TOKENS.fieldBorder,
                            color: FORM_TOKENS.ink,
                        }}
                    >
                        {removeLabel} ({problemSerials.length})
                    </Button>
                    <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel }}>
                        The rest of the form is kept, so you can fix these and submit again.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default SerialReportTable;
